"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setFirebaseAuth = setFirebaseAuth;
exports.initSupabase = initSupabase;
exports.initSupabaseWithAuth = initSupabaseWithAuth;
exports.getSupabase = getSupabase;
exports.resetSupabase = resetSupabase;
const supabase_js_1 = require("@supabase/supabase-js");
// Create a singleton instance that will be auto-initialized
let supabaseInstance = null;
// Firebase auth instance (will be set by the app)
let firebaseAuthInstance = null;
/**
 * Set the Firebase Auth instance to use for getting access tokens.
 * Call this before initializing Supabase.
 */
function setFirebaseAuth(firebaseAuth) {
    firebaseAuthInstance = firebaseAuth;
}
/**
 * Auto-initialize Supabase client from environment variables.
 * This runs automatically when the module is imported.
 *
 * Required environment variables:
 * - REACT_APP_SUPABASE_URL (React apps)
 * - REACT_APP_SUPABASE_ANON_KEY (React apps)
 * - SUPABASE_URL (Node/backend apps)
 * - SUPABASE_ANON_KEY (Node/backend apps)
 */
function autoInitSupabase() {
    // Try React environment variables first, then Expo, then Node
    const url = process.env.REACT_APP_SUPABASE_URL || process.env.EXPO_PUBLIC_SUPABASE_URL || process.env.SUPABASE_URL;
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;
    if (url && anonKey) {
        supabaseInstance = (0, supabase_js_1.createClient)(url, anonKey, {
            db: {
                schema: 'trader'
            },
            global: {
                headers: {
                    'x-client-info': 'trader-dashboard'
                }
            },
            auth: {
                autoRefreshToken: false,
                persistSession: false,
                detectSessionInUrl: false,
            },
            accessToken: async () => {
                // Get Firebase JWT token
                if (firebaseAuthInstance?.currentUser) {
                    try {
                        const token = await firebaseAuthInstance.currentUser.getIdToken(false);
                        console.log('🔑 Sending Firebase token to Supabase:', token?.substring(0, 20) + '...');
                        return token;
                    }
                    catch (error) {
                        console.error('❌ Error getting Firebase token:', error);
                        return null;
                    }
                }
                console.log('⚠️ No Firebase user, sending null token');
                return null;
            }
        });
        console.log('✅ Supabase client initialized with Firebase Auth integration');
    }
    else {
        console.warn('Supabase environment variables not found. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY ' +
            '(or SUPABASE_URL and SUPABASE_ANON_KEY for Node apps)');
    }
}
// Auto-initialize on module load
autoInitSupabase();
/**
 * Manually initialize the Supabase client (optional, for custom configuration).
 * Only needed if you want to override the auto-initialization.
 */
function initSupabase(url, anonKey) {
    supabaseInstance = (0, supabase_js_1.createClient)(url, anonKey, {
        db: {
            schema: 'trader'
        }
    });
    return supabaseInstance;
}
/**
 * Initialize Supabase client with Firebase Auth integration.
 * Use this for platforms where auto-initialization from env vars doesn't work (e.g., React Native).
 * Call setFirebaseAuth() before calling this function.
 */
function initSupabaseWithAuth(url, anonKey) {
    supabaseInstance = (0, supabase_js_1.createClient)(url, anonKey, {
        db: {
            schema: 'trader'
        },
        global: {
            headers: {
                'x-client-info': 'trader-dashboard-mobile'
            }
        },
        auth: {
            autoRefreshToken: false,
            persistSession: false,
            detectSessionInUrl: false,
        },
        accessToken: async () => {
            if (firebaseAuthInstance?.currentUser) {
                try {
                    return await firebaseAuthInstance.currentUser.getIdToken(false);
                }
                catch (error) {
                    console.error('Error getting Firebase token:', error);
                    return null;
                }
            }
            return null;
        }
    });
    return supabaseInstance;
}
/**
 * Get the Supabase client instance.
 * Throws if not initialized.
 */
function getSupabase() {
    if (!supabaseInstance) {
        throw new Error('Supabase client not initialized. Ensure environment variables are set:\n' +
            '- REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY (React apps)\n' +
            '- SUPABASE_URL and SUPABASE_ANON_KEY (Node apps)');
    }
    return supabaseInstance;
}
/**
 * Reset the Supabase instance (mainly for testing)
 */
function resetSupabase() {
    supabaseInstance = null;
}
